import { useState } from 'react';
import { Paper, Stack, TextInput, Button, Group, Text, Grid, Badge, Divider } from '@mantine/core';
import { IconDeviceFloppy, IconBuilding, IconAt, IconCalendar } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuthStore } from '@/stores/authStore';
import { useUpdateOrganizationSettings } from '@/hooks/api/useOrganizationSettings';

export function GeneralSettings() {
  const { organization, organizationId } = useOrganization();
  const user = useAuthStore((state) => state.user);
  const updateSettings = useUpdateOrganizationSettings();

  const [orgName, setOrgName] = useState(organization?.name || '');
  const [hasChanges, setHasChanges] = useState(false);

  const handleNameChange = (value: string) => {
    setOrgName(value);
    setHasChanges(value !== organization?.name);
  };

  const handleSave = async () => {
    if (!organizationId) return;

    // This would require a separate mutation to update the organization name
    // For now, we'll just show the UI - you may need to implement this API endpoint
    // await updateOrganizationName.mutateAsync({ organizationId, name: orgName });

    setHasChanges(false);
  };

  const handleReset = () => {
    setOrgName(organization?.name || '');
    setHasChanges(false);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Stack gap="lg">
      <Paper withBorder p="md">
        <Stack gap="md">
          <div>
            <Text fw={600} size="lg" mb="xs">
              Organization Information
            </Text>
            <Text c="dimmed" size="sm">
              Basic information about your organization
            </Text>
          </div>

          <Divider />

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Organization Name"
                placeholder="Enter organization name"
                value={orgName}
                onChange={(e) => handleNameChange(e.currentTarget.value)}
                leftSection={<IconBuilding size={16} />}
                description="This name will appear throughout the application"
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Plan Status
                </Text>
                <Badge
                  size="lg"
                  color={organization?.trial_mode ? 'yellow' : 'green'}
                  variant="light"
                >
                  {organization?.trial_mode ? 'Trial' : 'Active'}
                </Badge>
                {organization?.trial_mode && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {organization.asset_limit} asset limit
                  </Text>
                )}
              </div>
            </Grid.Col>
          </Grid>

          {hasChanges && (
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleReset}>
                Reset
              </Button>
              <Button
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={handleSave}
                loading={updateSettings.isPending}
              >
                Save Changes
              </Button>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap="md">
          <div>
            <Text fw={600} size="lg" mb="xs">
              Account Information
            </Text>
            <Text c="dimmed" size="sm">
              Non-editable information about your account
            </Text>
          </div>

          <Divider />

          <Group grow>
            <TextInput
              label="Primary Email"
              value={user?.email || 'N/A'}
              readOnly
              disabled
              leftSection={<IconAt size={16} />}
            />

            <TextInput
              label="Account Created"
              value={formatDate(user?.created_at)}
              readOnly
              disabled
              leftSection={<IconCalendar size={16} />}
            />
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
